import { useCallback, useContext, useEffect, useState } from 'react';
import { Dialog } from '@material-ui/core';
import { NoteEditor, NoteRailMarker } from 'components';
import { RailNoteCard } from 'components/UserContentCards';
import { SCREEN_SIZE_CATEGORIES } from 'constants';
import {
  UserContentItemModifiersContext,
  UserContentItemValuesContext,
  DocumentValuesContext,
  OfflineValuesContext,
  NetworkStatusValuesContext,
  UserSessionValuesContext,
  ViewportValuesContext,
} from 'contexts';

const useRailNoteItems = () => {
  const { frame } = useContext(DocumentValuesContext);
  const { isOffline } = useContext(NetworkStatusValuesContext);
  const { checkIsChapterDownloaded } = useContext(OfflineValuesContext);
  const {
    clearAllUserContent,
    focusInlineHighlight,
    onDialogClose,
    onDialogOpen,
    setNoteToDisplayAsDialog,
  } = useContext(UserContentItemModifiersContext);
  const {
    currentDocumentNotes,
    noteCardsRef,
    noteToDisplayAsDialog,
    noteToEdit,
    selectedNote,
  } = useContext(UserContentItemValuesContext);
  const { user } = useContext(UserSessionValuesContext);
  const { viewport } = useContext(ViewportValuesContext);

  const shouldShowIndicatorOnly =
    viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.TABLET;

  const [railNoteItems, setRailNoteItems] = useState([]);
  const [lastNoteToEdit, setLastNoteToEdit] = useState(null);
  const [lastActiveElement, setLastActiveElement] = useState();

  const addNewNote = useCallback(
    (railNoteItems) => {
      // Display 'create note form' NoteEditor to create new note.
      if (
        !noteToDisplayAsDialog && // No note is being already being displayed
        selectedNote && // A note is selected and
        noteToEdit && // it is the note being edited and
        !noteToEdit.isSaved && // it is not saved yet and
        !currentDocumentNotes.includes(noteToEdit) // it is not already in the list of notes in the document
      ) {
        const newNoteEditorJSX = (
          <NoteEditor
            elevation={8}
            note={noteToEdit}
            onClose={() => {
              if (!noteToEdit.key) {
                frame.focus();
              }
            }}
            onSave={async (properties) =>
              await noteToEdit.change(properties).save()
            }
            variant={'elevation'}
          />
        );

        railNoteItems.push({
          element: newNoteEditorJSX,
          key: `editing-${noteToEdit.key}`,
          bookLocation: noteToEdit.bookLocation,
        });

        return railNoteItems;
      }
    },
    [
      currentDocumentNotes,
      frame,
      noteToDisplayAsDialog,
      noteToEdit,
      selectedNote,
    ],
  );

  const focusOptionsMenu = useCallback(
    (note) => {
      if (!lastNoteToEdit || note.key !== lastNoteToEdit.key) {
        return;
      }

      noteCardsRef.current[lastNoteToEdit.key].querySelector('button')?.focus();
      setLastNoteToEdit(null);
    },
    [lastNoteToEdit, noteCardsRef],
  );

  const createNoteCardsList = useCallback(
    (railNoteItems) => {
      for (let note of currentDocumentNotes ?? []) {
        // don't render notes for markers not downloaded when offline
        if (isOffline && !checkIsChapterDownloaded(note.marker)) {
          continue;
        }

        const isCollaborationContent = !user.created(note);

        // don't render collaboration notes when offline
        if (isOffline && isCollaborationContent) {
          continue;
        }

        const noteElement = shouldShowIndicatorOnly ? (
          <>
            <NoteRailMarker
              colorInfo={isCollaborationContent ? 'collaboration' : note.color}
              id={`rail-marker-${note.id}`}
              onClick={(e) => {
                setNoteToDisplayAsDialog(note);
                setLastActiveElement(e.target);
              }}
            />

            {noteToDisplayAsDialog && noteToDisplayAsDialog.id === note.id ? (
              <Dialog
                open={true}
                onClose={(e) => {
                  e.stopPropagation();

                  if (e.key === 'Escape' && !lastActiveElement) {
                    focusInlineHighlight(noteToDisplayAsDialog);
                  }
                  clearAllUserContent();
                  setLastActiveElement();
                  onDialogClose('railNoteDialog');
                }}
                TransitionProps={{
                  onEnter: () => onDialogOpen('railNoteDialog'),
                }}
                disablePortal
              >
                <RailNoteCard
                  isCollaborationContent={isCollaborationContent}
                  userContentItem={noteToDisplayAsDialog}
                  ref={(el) => (noteCardsRef.current[note.key] = el)}
                />
              </Dialog>
            ) : null}
          </>
        ) : (
          <RailNoteCard
            focusOptionsMenu={focusOptionsMenu}
            isCollaborationContent={isCollaborationContent}
            onKeyUp={(e) => {
              e.stopPropagation();
              if (e.key === 'Escape') {
                focusInlineHighlight(note);
              }
            }}
            onNoteEditorClose={() => {
              if (!noteToEdit?.key) {
                frame.focus();
              } else {
                setLastNoteToEdit(noteToEdit);
              }
            }}
            ref={(el) => (noteCardsRef.current[note.key] = el)}
            userContentItem={note}
          />
        );

        railNoteItems.push({
          element: noteElement,
          key: note.key,
          bookLocation: note.bookLocation,
        });
      }

      return railNoteItems;
    },
    [
      checkIsChapterDownloaded,
      currentDocumentNotes,
      focusInlineHighlight,
      focusOptionsMenu,
      isOffline,
      lastActiveElement,
      noteCardsRef,
      noteToDisplayAsDialog,
      noteToEdit,
      onDialogClose,
      onDialogOpen,
      setNoteToDisplayAsDialog,
      shouldShowIndicatorOnly,
      user,
      clearAllUserContent,
      frame,
    ],
  );

  useEffect(
    function createRailItems() {
      let noteItemsList = [];
      addNewNote(noteItemsList);
      createNoteCardsList(noteItemsList);

      setRailNoteItems(noteItemsList);
    },
    [addNewNote, createNoteCardsList],
  );

  return railNoteItems;
};

export default useRailNoteItems;
